import { useMemo } from 'react';
import { ProductType } from '../types';
import { getTooltipText, getSavingsText, calculateTotalSavings } from '../config/savingsConfig';

interface UseSavingsCalculatorReturn {
  tooltipText: string;
  getBenefitText: (slideId: string) => string | null;
  getTotalSavings: (slideId: string) => string;
}

/**
 * Hook per calcolare i risparmi dinamici in base al prodotto, ZIP flag e CAP FVG
 * 
 * Questo hook supporta tre livelli di configurazione:
 * - base: valori di default
 * - overrideWhenFalse: valori per comuni > 50k abitanti
 * - fvg: valori speciali per la regione Friuli Venezia Giulia
 * 
 * Le funzioni getSavingsText e calculateTotalSavings gestiscono internamente
 * il mapping degli ID slide alle chiavi di configurazione corrette
 * (es. decision_stage -> install_timeline)
 */
export function useSavingsCalculator(
  productType?: ProductType,
  zipFlag?: boolean | null,
  zipCode?: string
): UseSavingsCalculatorReturn {
  // Tooltip text basato sul tipo di prodotto
  const tooltipText = useMemo(() => {
    return getTooltipText(productType);
  }, [productType]);

  // Funzione per ottenere il testo del beneficio per una slide
  // getSavingsText gestisce internamente la mappatura slideId -> benefitKey
  const getBenefitText = useMemo(() => {
    return (slideId: string) => getSavingsText(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  // Funzione per ottenere il totale cumulativo per una slide
  // calculateTotalSavings gestisce internamente la mappatura slideId -> benefitKey
  const getTotalSavings = useMemo(() => {
    return (slideId: string) => calculateTotalSavings(slideId, productType, zipFlag, zipCode);
  }, [productType, zipFlag, zipCode]);

  return {
    tooltipText,
    getBenefitText,
    getTotalSavings,
  };
}
